import { useEffect, useState } from 'react';
import { Image3D } from '~/domain/patient/image3d';

const THUMBNAIL_SIZE = 36;

export function useImageThumbnail(image: Image3D | undefined) {
    const [dataUrl, setDataUrl] = useState<string | null>(null);

    useEffect(() => {
        if (!image) {
            setDataUrl(null);
            return;
        }

        const timeoutId = requestIdleCallback(() => {
            setDataUrl(generateThumbnail(image));
        });

        return () => cancelIdleCallback(timeoutId);
    }, [image]);

    return dataUrl;
}

function generateThumbnail(image: Image3D): string | null {
    const { grid, pixelData } = image;
    const { sizeX: cols, sizeY: rows, sizeZ: slices } = grid;

    if (!pixelData.length || !cols || !rows) {
        return null;
    }

    const windowLevel = image.getDefaultWindowLevel();

    // Extract middle slice with windowing applied
    const sliceStart = Math.floor(slices / 2) * rows * cols;
    const windowRange = windowLevel.window;
    const windowMin = windowLevel.level - windowRange / 2;
    const scale = 255 / windowRange;

    // Create RGBA buffer - Uint8ClampedArray auto-clamps to 0-255
    const rgba = new Uint8ClampedArray(cols * rows * 4);
    for (let i = 0, j = 0; i < cols * rows; i++, j += 4) {
        const v = (pixelData[sliceStart + i] - windowMin) * scale;
        rgba[j] = rgba[j + 1] = rgba[j + 2] = v;
        rgba[j + 3] = 255;
    }

    // Render to thumbnail canvas
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = THUMBNAIL_SIZE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        return null;
    }

    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, THUMBNAIL_SIZE, THUMBNAIL_SIZE);

    // Create temp canvas for the slice, then scale it down
    const temp = document.createElement('canvas');
    temp.width = cols;
    temp.height = rows;
    const tempCtx = temp.getContext('2d');
    if (!tempCtx) {
        return null;
    }
    tempCtx.putImageData(new ImageData(rgba, cols, rows), 0, 0);

    // Draw scaled and centered
    const s = Math.min(THUMBNAIL_SIZE / cols, THUMBNAIL_SIZE / rows);
    const w = cols * s;
    const h = rows * s;
    ctx.drawImage(
        temp,
        (THUMBNAIL_SIZE - w) / 2,
        (THUMBNAIL_SIZE - h) / 2,
        w,
        h
    );

    return canvas.toDataURL('image/png');
}
